import { useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { dmSocket, type DmEvent } from "../lib/realtime/dmSocket";

/**
 * Headless component that keeps the DM websocket alive across the entire app,
 * so the user is marked "Online" to others even when they are not viewing
 * DirectChatPage, and so incoming messages get a "delivered" receipt.
 *
 * It no longer owns a socket. It used to open its own connection, which meant
 * a user sitting on the chat page held TWO DM sockets — both authenticating,
 * both registering presence, and both receiving every broadcast, so each
 * incoming message was acked twice. Both this component and DirectChatPage now
 * share the ref-counted singleton in lib/realtime/dmSocket, which also gives
 * this listener the jittered reconnect and online/visibility handling it
 * previously lacked.
 */
export default function DmGlobalListener() {
    const { user } = useAuth();

    useEffect(() => {
        if (!user) return;

        const onEvent = (payload: DmEvent) => {
            if (payload.type === "dm:message:new") {
                const message = payload.message as
                    | { id: string; sender_id: string }
                    | undefined;
                const threadId = payload.thread_id as string | undefined;
                if (message && message.sender_id !== user.id) {
                    dmSocket.send({
                        type: "dm:message:delivered",
                        thread_id: threadId,
                        message_id: message.id,
                    });
                }
                return;
            }

            // Auto-deliver everything missed while offline (sent on connect).
            if (payload.type === "dm:messages:missed") {
                const messages =
                    (payload.messages as Array<{
                        id: string;
                        thread_id: string;
                        sender_id: string;
                    }>) ?? [];
                for (const msg of messages) {
                    if (msg.sender_id !== user.id) {
                        dmSocket.send({
                            type: "dm:message:delivered",
                            thread_id: msg.thread_id,
                            message_id: msg.id,
                        });
                    }
                }
            }
        };

        return dmSocket.subscribe(onEvent);
    }, [user]);

    return null;
}
